import { useMemo } from 'react';
import {
  Box,
  ButtonGroup,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useMediaQuery,
  Theme,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { DatePeriodFilters } from '@/utils/charts';
import styles from './ChartDateFilter.styles';

export type CustomDates = {
  start?: Date | undefined;
  end?: Date | undefined;
} | null;

export type CustomDatesError = {
  start?: string;
  end?: string;
} | null;

type ChartDateFilterComponentProps = {
  currentFilter: DatePeriodFilters;
  values: (string | DatePeriodFilters)[];
  handleChangeFilter: (value: DatePeriodFilters) => void;
  handleChangeCustomDate: () => void;
  toggleCustomDateDialog: () => void;
  handleSetCustomDates: (period: 'start' | 'end', date?: Date | null) => void;
  customDates: CustomDates;
  customDatesError: CustomDatesError;
  disabledFilters?: DatePeriodFilters[];
  hasCustomDate?: boolean;
  isCustomDateDialogOpen?: boolean;
};

const ChartDateFilterComponent = function ({
  currentFilter,
  values,
  disabledFilters = [],
  handleChangeFilter,
  hasCustomDate = false,
  handleSetCustomDates,
  customDates,
  customDatesError,
  handleChangeCustomDate,
  toggleCustomDateDialog,
  isCustomDateDialogOpen = false,
}: ChartDateFilterComponentProps): JSX.Element {
  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const filterList = useMemo(() => {
    return values.map((value) => {
      const option = value as DatePeriodFilters;

      return (
        <Button
          key={option}
          disabled={disabledFilters.includes(option)}
          variant={currentFilter === option ? 'contained' : 'outlined'}
          onClick={() => handleChangeFilter(option)}
          disableElevation
        >
          Last {option} month(s)
        </Button>
      );
    });
  }, [values, disabledFilters, currentFilter, handleChangeFilter]);

  const customDateButton = useMemo(() => {
    const option = DatePeriodFilters.CUSTOM_DATE;

    return (
      <Button
        key={option}
        variant={currentFilter === option ? 'contained' : 'outlined'}
        onClick={toggleCustomDateDialog}
        disableElevation
        data-testid="custom-date-button"
      >
        Custom Date
      </Button>
    );
  }, [currentFilter, toggleCustomDateDialog]);

  return (
    <>
      <Box sx={styles.filterContainer}>
        <ButtonGroup
          orientation={isMobileScreen ? 'vertical' : 'horizontal'}
          fullWidth={isMobileScreen}
          variant="outlined"
          aria-label="outlined button group"
        >
          {filterList}
          {hasCustomDate && customDateButton}
        </ButtonGroup>
      </Box>

      <Dialog
        open={isCustomDateDialogOpen}
        onClose={toggleCustomDateDialog}
        data-testid="set-custom-date-dialog"
      >
        <DialogTitle>Set custom dates to filter by</DialogTitle>

        <DialogContent>
          <Box paddingTop={1}>
            <DesktopDatePicker
              label="Start date"
              value={customDates?.start || null}
              onChange={(newDate) => handleSetCustomDates('start', newDate)}
              renderInput={(params) => (
                <TextField
                  size="small"
                  {...params}
                  helperText={customDatesError?.start}
                  fullWidth={isMobileScreen}
                  error={!!customDatesError?.start}
                  data-testid="custom-date-start-input"
                />
              )}
            />

            <DesktopDatePicker
              label="End date"
              minDate={customDates?.start}
              value={customDates?.end || null}
              onChange={(newDate) => handleSetCustomDates('end', newDate)}
              renderInput={(params) => (
                <TextField
                  sx={{ marginLeft: { xs: 0, sm: 2 }, marginTop: { xs: 2, sm: 0 } }}
                  size="small"
                  {...params}
                  helperText={customDatesError?.end}
                  fullWidth={isMobileScreen}
                  error={!!customDatesError?.end}
                  data-testid="custom-date-end-input"
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => handleChangeCustomDate()}
            disabled={!!customDatesError}
            data-testid="change-custom-date-button"
          >
            Set Dates
          </Button>
          <Button color="auxRed" onClick={toggleCustomDateDialog} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChartDateFilterComponent;
